import type { HeaderService, ListItem } from '@patsnap-utils/biz';
import { Site, SLD, DeployEnv } from '@patsnap-utils/biz';
import { getCurrentLanguage } from '@eureka/language-tool';
import '@patsnap-ui/icon/assets/solid/HistoryRecord.svg';
import '@patsnap-ui/icon/assets/userpackage.svg';
import '@patsnap-ui/icon/assets/solid/rocket.svg';
import '@patsnap-ui/icon/assets/solid/AI.svg';
import '@patsnap-ui/icon/assets/solid/credits.svg';
import { clientDeferred } from '@eureka/utils/client';
import { controlDeferred } from '@eureka/utils/control';
import { container } from '@eureka/services/src/container';
import { REGION } from '@eureka/utils/environment';
import { CommonApis } from '@eureka/services/src/apis/common.api';

type ILocale = 'cn' | 'en' | 'tw' | 'jp'

let isCreditsAvailable = false;
let isCountPackageAvailable = false;
let isPersonal = false;
let isPro = false;

const PACKAGE_STATUS_RETRY_DELAYS = [1000, 2000, 4000, 8000];

interface RechargeEligibilityResponse {
    data?: {
        has_pay_packages?: boolean;
    };
}

function sleep(ms: number) {
    return new Promise(resolve => {
        setTimeout(resolve, ms);
    });
}

// 兼容Vue2版本navbar 请勿修改为composables
async function updatePackageStatus() {
    try {
        const control = await controlDeferred.promise;
        isCreditsAvailable = !!control.credit_package;
        isCountPackageAvailable = !!control.count_package;
    } catch {
        isCreditsAvailable = false;
        isCountPackageAvailable = false;
    }
}

async function updatePackageStatusWithRetry(headerService: HeaderService, onStatusUpdated: () => void) {
    await updatePackageStatus();
    onStatusUpdated();

    if (!headerService.userCenterService.logged || isCreditsAvailable) {
        return;
    }

    for (const delay of PACKAGE_STATUS_RETRY_DELAYS) {
        await sleep(delay);

        try {
            const control = await CommonApis.getGlobalControl();
            isCreditsAvailable = !!control.credit_package;
            isCountPackageAvailable = !!control.count_package;
            onStatusUpdated();

            if (isCreditsAvailable) {
                return;
            }
        } catch {
            // 权限信息短暂未就绪时继续重试，避免新注册/支付回跳首刷缺菜单。
        }
    }
}

function updatePersonalStatus(headerService: HeaderService) {
    isPersonal = !headerService.account?._info?.company_id;
}

async function updateProStatus(headerService: HeaderService) {
    if (!headerService.userCenterService.logged) {
        isPro = false;
        return;
    }

    try {
        const res = await container.AccountHttp.get<RechargeEligibilityResponse>({
            url: `${process.env.VUE_APP_EUREKA_SERVICE_URI}/api/eureka/credits/personal/recharge-info`,
        });
        isPro = !!res.data?.has_pay_packages;
    } catch {
        isPro = false;
    }
}

export interface CustomLinksOptions {
    checkAgentSettingsPermission?: () => Promise<boolean>;
}

export async function customProductsLinkInUserCenter(headerService: HeaderService, options?: CustomLinksOptions) {
    const locale = getCurrentLanguage() as ILocale;
    const buildin = headerService.userCenterService.beforeRenderMenuItems
    let isFeiShu = false;
    let hasAgentSettingsPermission = false;

    updatePersonalStatus(headerService);

    const renderMenuItems = (items: ListItem[]) => {
        const _items = buildin(items);
        _items.splice(1, 0, {
            href: new Site({ sld: SLD.EUREKA, pathname: 'history' }).href,
            label: ({
                cn: '历史记录',
                en: 'History',
                jp: '履歴',
                tw: '歷史記錄',
            })[locale],
            icon: 'SolidHistoryRecord',
            key: 'history',
        })

        // 只在有权限时添加个性化设置菜单
        if (hasAgentSettingsPermission && !isFeiShu) {
            _items.splice(2, 0, {
                href: new Site({ sld: SLD.EUREKA, pathname: 'user-setting/agent-settings' }).href,
                label: ({
                    cn: '个性化设置',
                    en: 'customization',
                    jp: 'エージェント設定',
                    tw: '個性化設置',
                })[locale],
                icon: 'SolidAi',
                key: 'agent-settings',
            })
        }

        if (!isFeiShu && !(isPersonal && [DeployEnv.US, DeployEnv.EU, DeployEnv.STAGE].includes(REGION))) {
            _items.splice(0, 0, {
                href: new Site({ sld: SLD.EUREKA, pathname: 'user-setting/package' }).href,
                label: ({
                    cn: '所有版本',
                    en: 'All versions',
                    jp: 'すべてのバージョンになります',
                    tw: '所有版本',
                })[locale],
                icon: 'SolidRocket',
                key: 'version',
            })
        }

        if (!isPersonal && isCountPackageAvailable) {
            _items.splice(0, 0, {
                href: new Site({ sld: SLD.EUREKA, pathname: 'user-setting/amount' }).href,
                label: ({
                    cn: '用量',
                    en: 'Usage',
                    jp: '利用回数',
                    tw: '用量',
                })[locale],
                icon: 'Userpackage',
                key: 'package',
            })
        }

        if (isCreditsAvailable) {
            _items.splice(0, 0, {
                href: new Site({ sld: SLD.EUREKA, pathname: 'credits' }).href,
                label: ({
                    cn: '积分',
                    en: 'Credits',
                    jp: 'クレジット',
                    tw: '積分',
                })[locale],
                icon: 'SolidCredits',
                key: 'credits',
            })
        }

        if (isFeiShu) {
            _items.splice(-2, 2)
        }

        if (isPersonal && isPro) {
            const manageSubscriptionItem = {
                href: new Site({ sld: SLD.EUREKA, pathname: 'user-setting/package/management' }).href,
                label: ({
                    cn: '管理订阅',
                    en: 'Manage Subscription',
                    jp: 'サブスクリプション管理',
                    tw: '管理訂閱',
                })[locale],
                icon: 'SolidRocket',
                key: 'manageSubscription',
            };
            const creditsItemIndex = _items.findIndex(item => item.key === 'credits');

            if (creditsItemIndex !== -1) {
                _items.splice(creditsItemIndex + 1, 0, manageSubscriptionItem);
            } else {
                _items.unshift(manageSubscriptionItem);
            }
        }

        return _items;
    }

    const refreshMenuItems = () => {
        headerService.userCenterService.beforeRenderMenuItems = function beforeRenderMenuItems(items: ListItem[]) {
            return renderMenuItems(items);
        }
    }

    refreshMenuItems();

    clientDeferred.promise.then(client => {
        isFeiShu = client.isFeiShu;
        refreshMenuItems();
    }).catch(() => {
        isFeiShu = false;
        refreshMenuItems();
    });

    updatePackageStatusWithRetry(headerService, refreshMenuItems);

    updateProStatus(headerService).finally(refreshMenuItems);

    // 异步检查agent-settings权限
    if (options?.checkAgentSettingsPermission) {
        options.checkAgentSettingsPermission().then(permission => {
            hasAgentSettingsPermission = permission;
            refreshMenuItems();
        }).catch(() => {
            hasAgentSettingsPermission = false;
            refreshMenuItems();
        });
    }
}
